<template>
  <button
    :type="type"
    :class="buttonClasses"
    :data-variant="variant"
    :data-size="size"
    :data-icon-only="isIconOnly ? 'true' : undefined"
    :disabled="isEffectivelyDisabled"
    :aria-busy="loading"
    :aria-label="props.ariaLabel"
    @click="$emit('click', $event)"
  >
    <span
      v-if="loading"
      class="inline-flex shrink-0"
      aria-hidden="true"
    >
      <svg
        class="animate-spin h-4 w-4 text-current"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        />
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    </span>
    <BaseIcon
      v-else-if="icon"
      :size="iconSize"
      :variant="computedIconVariant"
      :class="iconClasses"
    >
      <component :is="icon" />
    </BaseIcon>
    <span
      v-if="loading && loadingText"
      class="min-w-0"
    >{{ loadingText }}</span>
    <slot v-else />
  </button>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue'
import BaseIcon from '@/components/ui/BaseIcon.vue'

interface Props {
  type?: 'button' | 'submit' | 'reset'
  variant?:
    | 'primary'
    | 'secondary'
    /** Border + muted surface; label uses text-text-secondary (modal helper style). Pair with icon-variant for a colored icon. */
    | 'secondary-muted'
    | 'success'
    | 'warning'
    | 'danger'
    | 'purple'
    | 'ghost'
    | 'ghost-primary'
    /** Subtle elevated surface + border (origin .btn default — bundle member actions). */
    | 'ghost-bordered'
    | 'ghost-warning'
    | 'ghost-danger'
    | 'outline'
    /** Inline text link — accent color, underline, no surface (Sign Builder reset links). */
    | 'link'
  size?: 'xs' | 'small' | 'medium' | 'large' | 'extra-large'
  disabled?: boolean
  /** When true, button is disabled and shows an inline spinner; use with slot or loadingText. */
  loading?: boolean
  /** If set while loading, replaces default slot content (spinner still shows). */
  loadingText?: string
  icon?: any
  iconSize?: 'small' | 'medium' | 'large'
  iconVariant?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'purple' | 'ghost'
  ariaLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  type: 'button',
  variant: 'primary',
  size: 'medium',
  disabled: false,
  loading: false,
  iconSize: 'small',
  iconVariant: undefined
})

const slots = useSlots()

const isEffectivelyDisabled = computed(() => props.disabled || props.loading)

// Compute icon variant - only pass variant if explicitly provided, otherwise undefined so icon inherits color
const computedIconVariant = computed(() => {
  // If iconVariant is explicitly provided, use it
  if (props.iconVariant !== undefined) {
    return props.iconVariant
  }
  
  // Return undefined so BaseIcon doesn't apply variant classes and inherits color from button text
  return undefined
})

// Icon classes - margin when there is slot content, no margin for icon-only
const iconClasses = computed(() => {
  const hasContent = slots.default?.()
  return hasContent ? 'sm:mr-1 md:mr-2' : ''
})

defineEmits<{
  click: [event: MouseEvent]
}>()

const isIconOnly = computed(() => Boolean(props.icon) && !slots.default?.())

const buttonClasses = computed(() => {
  const baseClasses = 'base-button-geom inline-flex items-center justify-center focus:outline-none focus:custom-ring transition-colors'
  
  // Design: primary blue-500/600; secondary/ghost = border + surface-hover + text secondary; danger solid red
  const variantClasses: Record<string, string> = {
    primary: 'bg-primary text-primary-foreground hover:bg-primary-hover disabled:bg-primary/50 disabled:text-primary-foreground/60',
    secondary: 'border border-border text-secondary-foreground hover:bg-surface-hover disabled:opacity-50',
    'secondary-muted':
      'border border-border bg-surface-secondary text-text-secondary hover:bg-surface-hover hover:text-text disabled:opacity-50 disabled:text-text-muted disabled:hover:text-text-muted',
    success: 'bg-success text-success-foreground hover:bg-success-hover disabled:bg-success/50 disabled:opacity-75',
    warning: 'bg-warning text-warning-foreground hover:bg-warning-hover disabled:bg-warning/50 disabled:opacity-75',
    danger: 'bg-danger text-danger-foreground hover:bg-danger-hover disabled:bg-danger/50 disabled:opacity-75',
    purple: 'bg-purple text-purple-foreground hover:bg-purple-hover disabled:bg-purple/50 disabled:opacity-75',
    ghost: 'text-text-secondary hover:bg-surface-hover disabled:text-text-muted disabled:hover:bg-transparent',
    'ghost-bordered':
      'border border-border bg-surface text-text hover:bg-surface-hover hover:border-[color:var(--line-strong,var(--color-border))] disabled:opacity-50 disabled:hover:bg-surface',
    'ghost-primary': 'text-text-secondary  hover:bg-badge-blue hover:text-badge-blue disabled:text-text-muted disabled:hover:bg-transparent disabled:hover:text-text-muted',
    'ghost-warning':
      'text-text-secondary hover:!bg-[var(--color-toast-warning-bg)] hover:!text-[var(--color-toast-warning-text)] disabled:text-text-muted disabled:hover:bg-transparent disabled:hover:text-text-muted',
    'ghost-danger':
      'text-text-secondary hover:bg-badge-red hover:text-badge-red active:brightness-95 disabled:text-text-muted disabled:hover:bg-transparent disabled:hover:text-text-muted',
    outline:
      'border border-border bg-transparent text-secondary-foreground hover:bg-surface-hover disabled:opacity-50',
    link:
      'bg-transparent border-0 text-primary underline hover:text-primary-hover disabled:text-text-muted disabled:no-underline disabled:opacity-50',
  }

  /** Icon-only: square control aligned to BaseInput field height (py-2.5 + line → ~2.5rem). */
  return [
    baseClasses,
    variantClasses[props.variant] || variantClasses.primary,
    props.loading ? 'gap-2' : '',
    isEffectivelyDisabled.value ? 'cursor-not-allowed' : 'cursor-pointer'
  ].filter(Boolean).join(' ')
})
</script>
